import { validDiskGb, virtualMachineImage, virtualMachineSize } from "./virtualMachineCatalog.ts";

export interface VirtualMachineHostCapacity {
  readonly supported: boolean;
  readonly cpuCount: number;
  readonly reservedCpuCount: number;
  readonly memoryBytes: number;
  readonly reservedMemoryBytes: number;
  readonly diskBytes: number;
  readonly reservedDiskBytes: number;
}

/** Reserves the parent's resources and every existing guest before admitting another VM. */
export function virtualMachineAdmission(
  host: VirtualMachineHostCapacity,
  request: { readonly image: string; readonly size: string; readonly diskGb: number },
  operation: "create" | "start" = "create",
) {
  const refused = (reason: string) => ({ outcome: "refused" as const, reason });
  if (!host.supported) return refused("virtualization-unavailable");
  if (!virtualMachineImage(request.image)) return refused("unknown-image");
  const size = virtualMachineSize(request.size);
  if (!size) return refused("unknown-size");
  if (!validDiskGb(request.diskGb)) return refused("invalid-disk");
  if (
    ![
      host.cpuCount,
      host.reservedCpuCount,
      host.memoryBytes,
      host.reservedMemoryBytes,
      host.diskBytes,
      host.reservedDiskBytes,
    ].every((value) => Number.isSafeInteger(value) && value >= 0)
  )
    return refused("invalid-capacity");
  if (size.vcpu > host.cpuCount - host.reservedCpuCount) return refused("cpu-limit");
  const memoryBytes = size.memoryGb * 1024 ** 3;
  const diskBytes = request.diskGb * 1024 ** 3;
  if (memoryBytes > host.memoryBytes - host.reservedMemoryBytes) return refused("memory-limit");
  if (operation === "create" && diskBytes > host.diskBytes - host.reservedDiskBytes)
    return refused("disk-limit");
  return { outcome: "allowed" as const, limits: { cpuCount: size.vcpu, memoryBytes, diskBytes } };
}
